import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { apiClient } from '@/utils/api'
import TransportRulesEditor from '@/components/TransportRulesEditor'
import DistrictAndSchoolSubscriptions from '@/components/DistrictAndSchoolSubscriptions'
import UpcomingSchoolEvents from '@/components/UpcomingSchoolEvents'

/**
 * Parent School & Transportation page.
 *
 * Two sections:
 *   1. Subscriptions — districts + schools the family follows. Uses the same
 *      HP Phoenix endpoints mobile has been using since school-onboarding.
 *   2. Transport Rules — per-kid rule editor from Phase 5b of
 *      `custody-aware-transport`.
 *
 * Event List / School Calendar tabs intentionally absent — those surfaces
 * will come back once the Phoenix endpoints for parent-facing events exist.
 */

type Kid = { id: string; display_name: string }

const ParentSchoolManagement: React.FC = () => {
  const [kids, setKids] = useState<Kid[]>([])
  const [kidsError, setKidsError] = useState<string | null>(null)
  const [kidsLoading, setKidsLoading] = useState(true)

  useEffect(() => {
    apiClient
      .get('/api/parent/children')
      .then((resp: any) => {
        const children = resp?.children || resp || []
        setKids(
          children
            .filter((k: any) => !k.is_parent && !k.is_admin)
            .map((k: any) => ({ id: k.id, display_name: k.display_name || k.username })),
        )
      })
      .catch((e: any) => setKidsError(e?.message || 'Failed to load kids'))
      .finally(() => setKidsLoading(false))
  }, [])

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/dashboard/home"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">School & Transportation</h1>
        <p className="text-gray-600 mt-1">
          Subscribe to your kids' schools and districts for calendar events, and set how each kid
          gets to and from school. Day-of-week and custody-phase rules take priority over your
          weekday default.
        </p>
      </div>

      {kidsError && (
        <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{kidsError}</div>
      )}
      {kidsLoading ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-gray-500 text-sm">Loading kids…</div>
      ) : kids.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-500">
          No kids found on this account.
        </div>
      ) : (
        <>
          <DistrictAndSchoolSubscriptions kids={kids} />
          <UpcomingSchoolEvents />
          <div className="space-y-6">
            {kids.map((k) => (
              <TransportRulesEditor key={k.id} userId={k.id} displayName={k.display_name} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ParentSchoolManagement
